import { useState } from "react";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { refundApi, refundQueryKeys, type Refund } from "../../shared/api/refund";
import { apiError } from "../../shared/api/client";
import { formatVndWithUnit, paymentMethodLabel } from "../../shared/format";
import type { PaymentMethod } from "../../shared/types/billing";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

/** Các tab trạng thái; `ALL` là "mọi trạng thái" (API nhận `undefined`). */
const STATUS_TABS = [
  ["REQUESTED", "Chờ chuyển trả"],
  ["EXECUTED", "Đã chuyển trả"],
  ["FAILED", "Không chuyển được"],
  ["ALL", "Tất cả"],
] as const;

/**
 * Tiền phải trả lại bệnh nhân (#18998).
 *
 * <p>Danh sách này là NGHĨA VỤ, không phải lịch sử: mỗi dòng còn ở đây nghĩa là tiền chưa về tay
 * bệnh nhân. Nó chỉ đóng khi có người xác nhận đã chuyển, kèm bằng chứng.
 */
export function RefundsPage() {
  const qc = useQueryClient();
  // Trạng thái đang xem nằm trên URL; mặc định là các khoản CHƯA chuyển trả.
  const { filters, set } = useUrlFilters({
    status: param.oneOf(STATUS_TABS.map(([value]) => value), "REQUESTED"),
  });
  const status = filters.status === "ALL" ? "" : filters.status;
  const rows = useQuery({
    queryKey: refundQueryKeys.list(status),
    queryFn: () => refundApi.list(status || undefined),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["billing", "refunds"] });

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Hoàn tiền bệnh nhân"
        description="Mỗi dòng còn ở đây là tiền chưa về tay bệnh nhân. Chuyển trả xong thì đánh dấu, kèm mã giao dịch."
      />

      <div className="mb-3 flex flex-wrap gap-2">
        {STATUS_TABS.map(([value, label]) => (
          <Button
            key={value}
            size="sm"
            variant={filters.status === value ? "default" : "outline"}
            onClick={() => set({ status: value })}
          >
            {label}
          </Button>
        ))}
      </div>

      {rows.isPending && <p className="text-sm text-muted-foreground">Đang tải…</p>}
      {rows.data?.length === 0 && (
        <Card>
          <CardContent className="p-5 text-sm text-muted-foreground">
            Không có khoản nào.
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {(rows.data ?? []).map((r) => (
          <RefundRow key={r.id} row={r} onDone={refresh} />
        ))}
      </div>
    </div>
  );
}

function RefundRow({ row, onDone }: { row: Refund; onDone: () => void }) {
  const [evidence, setEvidence] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pending = row.status === "REQUESTED";

  const execute = useMutation({
    mutationFn: () => refundApi.markExecuted(row.id, evidence.trim()),
    onSuccess: onDone,
    onError: (e) => setError(apiError(e).detail),
  });
  const fail = useMutation({
    mutationFn: () => refundApi.markFailed(row.id, evidence.trim()),
    onSuccess: onDone,
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <strong className="text-lg tabular-nums">{formatVndWithUnit(row.amountVnd)}</strong>
            <span className="ml-2 text-sm text-muted-foreground">
              hoá đơn <span className="font-mono">{row.invoiceNumber}</span> · thu bằng{" "}
              {paymentMethodLabel[row.paymentMethod as PaymentMethod] ?? row.paymentMethod}
            </span>
          </div>
          <StatusChip status={row.status} />
        </div>

        {row.destination && (
          <p className="text-sm">
            <span className="text-muted-foreground">Nội dung chuyển khoản gốc: </span>
            <span className="font-mono text-xs">{row.destination}</span>
          </p>
        )}
        {row.reason && (
          <p className="text-sm">
            <span className="text-muted-foreground">Lý do: </span>
            {row.reason}
          </p>
        )}
        {row.failureReason && (
          <p className="text-sm text-destructive">Không chuyển được: {row.failureReason}</p>
        )}

        {pending && (
          <>
            <Input
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              placeholder="Mã giao dịch đã chuyển trả, hoặc lý do không chuyển được"
            />
            <p className="text-xs text-muted-foreground">
              Bắt buộc ghi. Không có nó thì tháng sau không ai chứng minh được khoản này đã trả, và
              nó hiện lại ở mọi lần đối soát.
            </p>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={!evidence.trim() || execute.isPending}
                onClick={() => execute.mutate()}
              >
                Đã chuyển trả
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!evidence.trim() || fail.isPending}
                onClick={() => fail.mutate()}
              >
                Không chuyển được
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatusChip({ status }: { status: Refund["status"] }) {
  const map: Record<Refund["status"], [string, string]> = {
    REQUESTED: ["Chờ chuyển trả", "bg-amber-100 text-amber-800"],
    EXECUTED: ["Đã chuyển trả", "bg-emerald-100 text-emerald-800"],
    FAILED: ["Không chuyển được", "bg-destructive/10 text-destructive"],
    CANCELLED: ["Đã huỷ", "bg-muted text-muted-foreground"],
  };
  const [label, cls] = map[status];
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}
